import React, { useEffect } from 'react';
import { Wallet, ShieldAlert, CheckCircle, Loader2, CreditCard, Landmark, Clock, TrendingUp} from 'lucide-react';
import { usePaymentDocuments } from '../../../hooks/expertDashboardHooks/usePaymentDocuments';
import { useExpertWallet } from '../../../hooks/paymentHooks/expertwalletHooks/useExpertWallet'; 
import PaymentDocumentForm from './PaymentDocumentForm';

export default function ExpertWalletCheckout() {
  const { 
    walletBalance, 
    allTimeEarnings, 
    totalRedeemed, 
    thisMonthEarnings,   
    monthlyBreakdown,    
    isVerified: dashboardIsVerified, 
    loading: dashboardLoading, 
    error: dashboardError 
  } = useExpertWallet();

  const { 
    documents, 
    loading: formLoading, 
    uploading, 
    error: formError, 
    fetchDocuments, 
    uploadFile, 
    submitDocuments 
  } = usePaymentDocuments();

  // Load document status on mount
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Bulletproof Check: Trust the documents database record first
  const isFullyVerified = documents?.verification_status === 'approved' || dashboardIsVerified;

  // --- UI RENDERERS ---
  const renderStatCard = (title: string, amount: number, icon: React.ReactNode) => (
    <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm flex items-center space-x-4">
      <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-blue-600">
        {icon}
      </div>
      <div>
        <p className="text-sm text-slate-500 font-medium">{title}</p>
        <h3 className="text-2xl font-bold text-slate-800">₹{Number(amount).toFixed(2)}</h3>
      </div>
    </div>
  );

  // Generate static breakdown data mapping for calendar months
  const monthsNames = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // 🟢 FIXED: Safe object destructuring to prevent toFixed runtime execution crashes
  const breakdownData = monthsNames.map((month, index) => {
    const rawItem = monthlyBreakdown?.[index];
    
    // Fallback checks support both the new aggregated objects and previous array primitives smoothly
    const grossEarning = rawItem && typeof rawItem === 'object' 
      ? (Number(rawItem.grossEarning) || 0) 
      : (Number(rawItem) || 0);

    const debitedAmount = rawItem && typeof rawItem === 'object'
      ? (Number(rawItem.debitedAmount) || 0)
      : 0;
      
    return { month, grossEarning, debitedAmount };
  });

  // Prevent UI flashing while initial data loads
  if (dashboardLoading || formLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[500px]">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      
      {/* 1. TOP STATS ROW */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {renderStatCard("Wallet Balance", walletBalance, <Wallet />)}
        {renderStatCard("All-Time Earnings", allTimeEarnings, <Landmark />)}
        {renderStatCard("Total Redeemed", totalRedeemed, <CreditCard />)}
        {renderStatCard("This Month's Earning", thisMonthEarnings || 0, <TrendingUp />)}
      </div>

      {(formError || dashboardError) && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg text-sm border border-red-200">
          {formError || dashboardError}
        </div>
      )}

      {/* 2. ANNUAL MONTHLY BREAKDOWN STYLISH CARD GRID */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 space-y-4">
        <div>
          <h2 className="text-base font-bold text-slate-800">Annual Earnings Breakdown</h2>
          <p className="text-xs text-slate-400 mt-0.5">Consolidated summary overview of your consultation session payouts across the year.</p>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {breakdownData.map((item) => (
            <div 
              key={item.month} 
              className="p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-all flex flex-col justify-between min-h-[95px] hover:shadow-sm"
            >
              <span className="text-xs text-slate-500 font-semibold tracking-wide">{item.month}</span>
              
              <div className="flex flex-col space-y-0.5 mt-2">
                {/* Gross Earnings Display Line */}
                <div className="flex items-center justify-between text-slate-400 text-[10px]">
                  <span>Earned</span>
                  <span className="font-bold text-slate-900 font-mono text-sm">₹{item.grossEarning.toFixed(2)}</span>
                </div>
                
                {/* Dynamic Debited Payouts Display Line */}
                <div className="flex items-center justify-between text-slate-400 text-[10px]">
                  <span>Debited</span>
                  <span className={`font-mono text-xs font-semibold ${item.debitedAmount > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                    {item.debitedAmount > 0 ? `-₹${item.debitedAmount.toFixed(2)}` : '₹0.00'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 3. VERIFICATION STATUS OR FORM */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        
        {/* Banner Conditionals */}
        {isFullyVerified ? (
          <div className="bg-green-50 p-6 flex items-start space-x-4 border-b border-green-100">
            <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-bold text-green-800">Account Verified</h2>
              <p className="text-green-700 text-sm mt-1">Your documents have been approved. You can now withdraw your funds directly to your bank account.</p>
            </div>
          </div>
        ) : documents?.verification_status === 'pending' ? (
           <div className="bg-yellow-50 p-6 flex items-start space-x-4 border-b border-yellow-100">
             <Clock className="w-8 h-8 text-yellow-600 flex-shrink-0" />
             <div>
               <h2 className="text-lg font-bold text-yellow-800">Verification Pending</h2>
               <p className="text-yellow-700 text-sm mt-1">Payment document uploaded and waiting to be approved by admin. Please wait 3 - 5 business days.</p>
             </div>
           </div>
        ) : documents?.verification_status === 'rejected' ? (
          <div className="bg-red-50 p-6 flex items-start space-x-4 border-b border-red-100">
            <ShieldAlert className="w-8 h-8 text-red-500 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-bold text-red-800">Verification Rejected</h2>
              <p className="text-red-700 text-sm mt-1">
                Your document verification failed. Please review the feedback below and resubmit.
              </p>
              {documents?.admin_remarks && (
                <div className="mt-3 p-3 bg-red-100/50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-900 font-semibold">Admin Feedback:</p>
                  <p className="text-sm text-red-800 italic">"{documents.admin_remarks}"</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 p-6 flex items-start space-x-4 border-b border-blue-100">
            <ShieldAlert className="w-8 h-8 text-blue-500 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-bold text-blue-900">Action Required: Verify KYC to Withdraw</h2>
              <p className="text-blue-700 text-sm mt-1">Oh! Seems like you haven't verified your payment documents. Verify first to proceed to checkout and get your verified badge.</p>
            </div>
          </div>
        )}

        {/* Render the form ONLY if they are completely unverified */}
        {!isFullyVerified && (
          <PaymentDocumentForm 
            initialData={documents}
            uploadFile={uploadFile}
            submitDocuments={submitDocuments}
            loading={formLoading}
            uploading={uploading}
          />
        )}
      </div>
    </div>
  );
}